import axios, { HttpStatusCode } from 'axios';

import config from '@/config';
import { router } from '@/router/router';

axios.defaults.baseURL = config.apiUrl;
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

const DEFAULT_ERROR_RESPONSE = {
  statusCode: null,
  exception: 'An unknown error occurred',
};

axios.interceptors.request.use(config => {
  /*
   * If the data being sent in the request is form data, let the browser
   * automatically set the Content-Type header to multipart/form-data,
   * otherwise send it as application/json for all other requests
   */
  if (config.data && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

axios.interceptors.response.use(response => {
  return response;
}, async err => {
  // Handle API call failures due to a problem with the user's internet connection
  if (!err.response) {
    return Promise.reject(DEFAULT_ERROR_RESPONSE);
  }

  /*
   * Don't redirect in development for failed API requests. This makes it easier for debugging, because if there's a problem,
   * the developer doesn't have to navigate back to where the problem happened before being able to debug
   */
  if (import.meta.env.DEV) {
    return Promise.reject(err);
  }

  switch (err.response.status) {
    // If the API call didn't fail due to invalid login credentials or an expired session, redirect the user to the login page
    case HttpStatusCode.Unauthorized:
      if (!err.response.config.url.match(/\/?auth\/login/)) {
        await router.push({ name: 'app_auth_login' });
      }

      break;

    case HttpStatusCode.InternalServerError:
      await router.push({ name: 'app_server_error' });

      break;

    case HttpStatusCode.NotFound:
      await router.push({ name: 'app_not_found' });

      break;

    // If the server responds with a validation failed error, don't do anything, since the validation errors wil be handled by the component that made the API request
    case HttpStatusCode.UnprocessableEntity:
      break;

    case HttpStatusCode.Forbidden:
      await router.push({ name: 'app_access_denied' });

      break;

    default:
      await router.push({ name: 'app_unknown_error' });
  }

  return Promise.reject(err);
});

const defaultHeaders = {
  'Content-Type': 'application/json',
};

export const HttpClient = {
  async request(method, url, body = null, queryParams = null, headers = null, options ={}) {
    const config = { method, url, headers, params: queryParams, ...options };

    if (body) {
      config.data = body;
    }

    try {
      const response = await axios(config);

      return response.data;
    } catch (err) {
      if (err.response) {
        return err.response.data;
      }

      return { statusCode: null, exception: 'An unknown error occurred' };
    }
  },
  get(url, query, headers, options) {
    return this.request('GET', url, null, query, headers || defaultHeaders, options);
  },
  post(url, body, headers, options) {
    return this.request('POST', url, body, null, headers || defaultHeaders, options);
  },
  put(url, body, headers) {
    return this.request('PUT', url, body, null, headers || defaultHeaders);
  },
  delete(url, headers) {
    return this.request('DELETE', url, null, null, headers || defaultHeaders);
  },
};
